import { useEffect, useState } from 'react'

// material-ui
import { Button, Chip, MenuItem, Box } from '@mui/material'
import { Image as ImageIcon } from '@mui/icons-material'
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles'
import { PlusCircleOutlined, EyeOutlined, EditOutlined, DeleteOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'

// project import
import { useAuth } from 'services/hook/Auth'
import { useApiData } from 'services/hook/useApiData'
import { useAuthContext } from 'services/provider/AuthProvider';
import { useEventBusContext } from 'services/provider/EventBusProvider'
import { PageHeader } from 'components/PageHeader'
import CellRenderer from 'components/list/CellRenderer';
import { getPermissionKeys } from 'utils/util';
import { categoryList } from 'models/category';
import { EVENT_CATEGORY_ADD_INIT, EVENT_CATEGORY_DELETE_INIT, EVENT_CATEGORY_DETAIL_INIT, EVENT_CATEGORY_UPDATE_INIT, EVENT_CATEGORY_UPDATE_SUCCESS } from 'models/events/category.event';
import { CellMobileDetails } from './list/Category.List.Cell';
import { CategoryAddDialog } from './Category.Create';
import { CategoryEditDialog } from './Category.Update';
import { CategoryDetailDialog } from './Category.Detail.Quick';
import { CategoryDeleteDialog } from './Category.Delete';
import { CategoryListFilter } from './list/Category.List.Filter';
import DataGridList from '../../components/data-grid/DataGridList';
import { categoryToggleStatus } from '../../models/category';
import { getImageUrl, sanitizeFilters } from 'utils/helpers';

/**
 * List category page base component
 */
const CategoryList = () => {
    const permissionsKeys = getPermissionKeys('category');
    return (<>
        <PageHeader title="Categories" breadcrumbs={[{ path: '#', label: 'Categories' }]} />
        <CategoryListTable permissionsKeys={permissionsKeys} />
        {permissionsKeys.can_create ? <CategoryAddDialog /> : null}
        {permissionsKeys.can_update ? <CategoryEditDialog /> : null}
        {permissionsKeys.can_view ? <CategoryDetailDialog /> : null}
        {permissionsKeys.can_delete ? <CategoryDeleteDialog /> : null}
    </>)
}

/**
 * List all category in data grid
 */
const CategoryListTable = ({ permissionsKeys }) => {
    const { signOut } = useAuthContext();
    const eventbus = useEventBusContext();
    const is_mobile = useMediaQuery('(max-width:991px)');
    const initdata = { data: [], total: 0 }
    const initparams = { page: 0, limit: 10, sort: '', orderby: '', filters: {} }

    const colheadsx = {
        '& .MuiDataGrid-cellCheckbox' : { alignItems: 'center' },
        '& .MuiDataGrid-topContainer': { opacity: 0, visibility: 'hidden' },
        '& .MuiDataGrid-cell': { 
            borderBottom: '1px solid #c5c5c5a1 !important',
            fontSize: '0.875rem', // Reduced by 2px (from 1rem/16px to 14px)
            paddingTop: '10px !important', // Decreased top padding
            paddingBottom: '10px !important', // Decreased bottom padding
            outline: 'none !important', // Remove focus outline
            display: 'flex !important',
            alignItems: 'center !important', // Center align vertically
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        }
    }

    const desktopGridSx = {
        '& .MuiDataGrid-cell': {
            fontSize: '0.875rem', // Reduced by 2px (from 1rem/16px to 14px)
            paddingTop: '10px !important', // Decreased top padding
            paddingBottom: '10px !important', // Decreased bottom padding
            lineHeight: 1.5,
            outline: 'none !important', // Remove focus outline
            display: 'flex !important',
            alignItems: 'center !important', // Center align vertically
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-row': {
            minHeight: '48px !important', // Reduced row height
            borderBottom: '1px solid #e0e0e0 !important', // Border bottom for each row
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-cellContent': {
            fontSize: '0.875rem' // Reduced by 2px
        },
        '& .MuiDataGrid-columnHeader': {
            backgroundColor: '#f5f5f5',
            fontSize: '1rem', 
            fontWeight: 600,
            outline: 'none !important', 
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-columnHeaderTitle': {
            fontSize: '1rem', 
            fontWeight: 600,
            outline: 'none !important', 
            '&:focus': {
                outline: 'none !important'
            }
        },
        '& .MuiDataGrid-columnHeaderTitleContainer': {
            outline: 'none !important', 
            '&:focus': {
                outline: 'none !important'
            },
            '&:focus-within': {
                outline: 'none !important'
            }
        }
    }

    const [data, params, loading, loadData] = useApiData(initdata, initparams, (params, setData) => {
        // Sanitize filters to remove empty/null/undefined/empty array values
        const sanitizedFilters = sanitizeFilters(params.filters || {})
        // Convert 0-based page (frontend) to 1-based page (backend)
        const apiParams = {
            ...params,
            ...sanitizedFilters,
            page: (params.page || 0) + 1  // Convert 0-based to 1-based
        }
        categoryList(apiParams)
            .then((res) => {
                console.log(res);
                setData(res)
            })
            .catch((res) => {
                console.log('catch');
                                console.log(res);
                if(res?.status_code === 401) {
                    signOut();
                }
            })
    }, false)

    useEffect(() => {
        return eventbus.on(EVENT_CATEGORY_UPDATE_SUCCESS, () => {
            loadData(params)
        })
    }, [eventbus, params, loadData])

    return (
        <>
            <DataGridList
                rows={data.data}
                columns={!is_mobile ? columns : mobile_cols}
                getRowHeight={() => 'auto'}
                actions={!is_mobile ? <ListActions /> : <ListActionMenuItems />}
                loading={loading}
                filterkey='categorylist'       
                sx={!is_mobile ? desktopGridSx : colheadsx}
                checkboxSelection={!is_mobile ? false : false}
                columnHeaderHeight={!is_mobile ? 56 : 0} 
                filter={(filters, setFilters) => (<CategoryListFilter filters={filters} setFilters={setFilters} />)}
                onFilter={(values) => {
                    params['filters'] = values
                    loadData(params)
                }}
                search={(data) => {
                    loadData({ ...params, query: data })
                }}
                rowCount={data.total}
                paginationMode="server"
                paginationModel={{ page: params.page, pageSize: params.limit }}
                onPaginationModelChange={(pagination) => {
                    loadData({ ...params, page: pagination.page, limit: pagination.pageSize })
                }}
                sortingMode="server"
                onSortModelChange={(sortModel) => {
                    if (sortModel.length > 0) {
                        loadData({ ...params, orderby: sortModel[0].field, sort: sortModel[0].sort })
                    } else {
                        loadData({ ...params, orderby: '', sort: '' })
                    }
                }}
                onInit={(pagination, sortModel, seach, filters) => {
                    var inparam = { query: seach, filters: filters }
                    if (pagination != undefined) {
                        // Keep 0-based page for internal state (MUI DataGrid uses 0-based)
                        inparam = { ...inparam, page: pagination.page, limit: pagination.pageSize }
                    }
                    if (sortModel != undefined && sortModel.length > 0) {
                        inparam = { ...inparam, orderby: sortModel[0].field, sort: sortModel[0].sort }
                    }

                    loadData({ ...params, ...inparam })
                }}
            />
        </>
    )
}

//add list page global actions
const ListActions = () => {
    const eventbus = useEventBusContext()
    const { checkPermission } = useAuth()
    return (<>
        {checkPermission('category.create') ? <Button variant="contained" onClick={() => eventbus.fire(EVENT_CATEGORY_ADD_INIT)} endIcon={<PlusCircleOutlined />}> Add Category </Button> : null}
    </>)
}


//add list page global actions for mobile
const ListActionMenuItems = () => {
    const theme = useTheme();
    const eventbus = useEventBusContext()
    const { checkPermission } = useAuth()
    const can_create = checkPermission('category.create');
    const menuitemsx = { display: 'flex', alignItems: 'center', minHeight: 'auto', p: '8px 16px', '& .anticon': { color: theme.palette.primary.main, mr: '10px' } };
    return (<>
        {can_create && <MenuItem sx={menuitemsx}
            onClick={(e) => {
                e.stopPropagation()
                eventbus.fire(EVENT_CATEGORY_ADD_INIT)
            }} >
            <PlusCircleOutlined /> Add Category
        </MenuItem>}
    </>)
}

/**
 * Get props details data for cells.
 */
const getCategoryPropsData = (props) => {
    const events = { 
        detail_init: EVENT_CATEGORY_DETAIL_INIT, 
        update_init: EVENT_CATEGORY_UPDATE_INIT, 
        delete_init: EVENT_CATEGORY_DELETE_INIT,
    }

    const actions = {
        detail: { tooltip: 'View Details', menu: 'View Details', icon: <EyeOutlined />, event: events.detail_init },
        update: { tooltip: 'Edit Category', menu: 'Edit', icon: <EditOutlined />, event: events.update_init },
        delete: { tooltip: 'Delete Category', menu: 'Delete', icon: <DeleteOutlined style={{ color: '#ff4d4f' }} />, event: events.delete_init },
    }
    const propdetails = { actions: actions, events: events }
    return propdetails;
}

// Mobile column configuration
const mobile_cols = [
    { field: 'name', headerName: 'Detail', flex: 1, renderCell: (props) => (<CellMobileDetails item={props.row} propsDetails={getCategoryPropsData()} />) }
]


const CellStatus = ({ item, type, onStatusChange }) => {
    const [loading, setLoading] = useState(false);
    const [currentStatus, setCurrentStatus] = useState(item.status);

    const handleStatusClick = async () => {
        if (loading) return;

        try {
            setLoading(true);
            const response = await categoryToggleStatus(item.id);
            
            const newStatus = response?.data?.status ?? !currentStatus;
            setCurrentStatus(newStatus);
            
            if (onStatusChange) {
                onStatusChange({ ...item, status: newStatus });
            }

        } catch (error) {
            console.error('Failed to update status:', error);
        } finally {
            setLoading(false);
        }
    };

    const isActive = currentStatus === 1 || currentStatus === true || currentStatus === 'active';
    const bgColor = isActive ? '#e2ffc8' : '#f9e3e1';
    const fgColor = isActive ? '#389E0D' : '#CF1322';
    const label = isActive ? 'Active' : 'Inactive';
    const icon = isActive ? <CheckCircleOutlined style={{ color: fgColor }} /> : <CloseCircleOutlined style={{ color: fgColor }} />;

    return (
        <Chip 
            label={label}
            size="medium" 
            icon={icon}
            onClick={handleStatusClick}
            disabled={loading}
            sx={{ 
                mt: '5px', 
                color: fgColor, 
                backgroundColor: bgColor,
                cursor: loading ? 'wait' : 'pointer',
                opacity: loading ? 0.6 : 1,
                fontSize: '16px',
                '&:hover': {
                    opacity: 0.8,
                    transform: 'scale(1.05)'
                },
                transition: 'all 0.2s ease'
            }} 
        />
    );

};

const ImageCell = ({ imageUrl, alt, size = 50, fallbackIcon: FallbackIcon = ImageIcon }) => {
    const [imageError, setImageError] = useState(false);

    const handleImageError = () => {
        setImageError(true);
    };

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', py: 1 }}>
            {imageUrl && !imageError ? (
                <Box
                    component="img" src={getImageUrl(imageUrl)} alt={alt || 'Image'}
                    sx={{ width: size, height: size, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                    onError={handleImageError}
                />
            ) : (
                <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', width: size, height: size, borderRadius: 1, bgcolor: 'grey.100', color: 'text.secondary' }}>
                    <FallbackIcon fontSize="small" />
                </Box>
            )}
        </Box>
    );
};


// Desktop column configuration
const columns = [
    { field: 'image', headerName: 'Image', flex: .5, minWidth: 100, maxWidth: 100, sortable: false, renderCell: (props) => ( <ImageCell imageUrl={props.row.image_url} alt={props.row.name} size={50} /> )},
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 80, renderCell: (props) => ( <CellRenderer type="name" item={props.row} propsDetails={getCategoryPropsData(props.row)} /> ) },
    { field: 'description', headerName: 'Description', flex: 1, minWidth: 180, renderCell: (props) => (<CellRenderer type="text" item={props.row} field="description" />)  },
    { field: 'products_count', headerName: ' No.of.Products', flex: 0.5, minWidth: 50, renderCell: (props) => (<CellRenderer type="text" item={props.row} field="products_count" />) },
    { field: 'status', headerName: 'Status', flex: 1, maxWidth: 120, renderCell: (props) => (<CellStatus item={props.row} type="status" onStatusChange={() => {}} /> ) },    
    { field: 'action', headerName: 'Action', sortable: false, flex: 0.5, minWidth: 120, maxWidth: 120, renderCell: (props) => (<CellRenderer type="action" item={props.row} propsDetails={getCategoryPropsData(props.row)} />) }
]

export default CategoryList

